import { useStarGoal, useUnstarGoal } from "@coaching-culture/api-client";
import { GoalDto, ObjectiveDto } from "@coaching-culture/types";
import {
  Button,
  Circle,
  DateText,
  DotMenu,
  EmptyState,
  Flex,
  IconButton,
  Label,
  LinkButton,
  Loader,
  Panel,
  Text,
} from "@coaching-culture/ui";
import { useUser } from "auth";
import { useGoalTerminology } from "auth/OrgProvider";
import WidgetHeader from "components/WidgetHeader";
import { fromUnixTime } from "date-fns";
import { sortBy } from "lodash";
import {
  useDeleteGoal,
  useGoalFields,
  useGoalsForUser,
  useUpdateGoal,
} from "queries/performance";
import { useMyRelationships } from "queries/team";
import { useState } from "react";
import {
  FaBullseye,
  FaChevronDown,
  FaChevronRight,
  FaFlagCheckered,
  FaLeaf,
  FaLock,
  FaMedal,
  FaPen,
  FaPlusCircle,
  FaSearchPlus,
  FaStar,
  FaTrash,
} from "react-icons/fa";
import { PieChart } from "react-minimal-pie-chart";
import { Link } from "react-router-dom";
import styled, { useTheme } from "styled-components";
import { EditObjectiveModal } from "./GoalEditor";
import { GoalProgressDisplay } from "./GoalProgressDisplay";
import { TimePeriod } from "./TimePeriod";

const Row = styled(Flex)`
  width: 100%;
  &:nth-child(odd) {
    background-color: ${(props) => props.theme.colors.grey7};
  }
`;

const Col = styled(Flex)`
  position: relative;
`;

const Line = styled.span`
  position: absolute;
  left: 7px;
  width: 2px;
  display: block;
  background-color: ${(props) => props.theme.colors.grey4};
`;

const ObjLine = styled(Line)`
  bottom: -18px;
  top: -18px;

  &:after {
    content: "";
    display: block;
    height: 2px;
    width: 30px;
    left: 0px;
    background-color: ${(props) => props.theme.colors.grey4};
    position: absolute;
    top: 50%;
  }
`;

const ActionLine = styled(ObjLine)`
  bottom: 50%;
  top: -18px;

  &:after {
    top: 100%;
  }
`;

const DownLine = styled(Line)`
  bottom: -18px;
  top: calc(50% + 12px);
`;

Col.defaultProps = {
  alignItems: "center",
};

export type GoalDisplayProps = {
  goal: GoalDto;
  onChange: (val: GoalDto) => void;
  onDelete: () => void;
  onReview: () => void;
  onStar: () => void;
  userId: string;
};

export function GoalDisplay({
  goal,
  onChange,
  onDelete,
  onReview,
  onStar,
  userId,
}: GoalDisplayProps) {
  const [open, setOpen] = useState<boolean>(false);
  const terms = useGoalTerminology();
  const [editingObj, setEditingObj] = useState<ObjectiveDto | "new" | null>(
    null,
  );
  const [user] = useUser();
  const theme = useTheme();
  const { data: goalFields } = useGoalFields();
  const { data: relationships } = useMyRelationships();

  const onItemClick = (_: number, cmd: string) => {
    if (cmd === "delete") {
      onDelete();
    } else if (cmd === "review") {
      onReview();
    } else if (cmd === "star" || cmd === "unstar") {
      onStar();
    }
  };

  const getFriendlyName = (outcome: string) => {
    switch (outcome) {
      case "completedachieved":
        return <Label color="positive">Completed - Achieved</Label>;
      case "partially":
        return <Label color="warning">Closed - Partially Met</Label>;
      case "exceeded":
        return <Label color="positive">Completed - Exceeded</Label>;
      case "missed":
        return <Label color="danger">Closed - Missed</Label>;
      case "nla":
        return <Label>Closed - No Longer Applicable</Label>;
      case "open":
        return <Label color="warning">Reopened</Label>;
    }
  };

  const onObjectiveItemClick = (id: string) => (idx: number) => {
    if (idx === 1) {
      if (window.confirm("Are you sure?")) {
        onChange({
          ...goal,
          objectives: goal.objectives.filter((x) => x.id !== id),
        });
      }
    } else if (idx === 0) {
      const obj = goal.objectives.find((x) => x.id === id);
      if (obj != null) {
        setEditingObj(obj);
      }
    }
  };

  const setDeadline = (val: number) => {
    onChange({
      ...goal,
      deadline: val,
    });
  };

  const setObjectiveDeadline = (id: string, val: number) => {
    onChange({
      ...goal,
      objectives: goal.objectives.map((x) => ({
        ...x,
        deadline: x.id === id ? val : x.deadline,
      })),
    });
  };

  const saveObj = (values: ObjectiveDto) => {
    if (editingObj === "new") {
      // new objective
      onChange({
        ...goal,
        objectives: [...goal.objectives, values],
      });
    } else {
      onChange({
        ...goal,
        objectives: goal.objectives.map((x) =>
          x.id === values.id ? values : x,
        ),
      });
    }
    setEditingObj(null);
  };

  const objFields = goalFields?.filter((x) => x.usage !== "goal") ?? [];

  const sortedObjs = goal.objectives.sort((a, b) => {
    if (
      fromUnixTime(a.createdOn).setHours(0, 0, 0, 0) <
      fromUnixTime(b.createdOn).setHours(0, 0, 0, 0)
    ) {
      return -1;
    } else if (
      fromUnixTime(a.createdOn).setHours(0, 0, 0, 0) <
      fromUnixTime(b.createdOn).setHours(0, 0, 0, 0)
    ) {
      return 1;
    } else if (
      fromUnixTime(a.createdOn).setHours(0, 0, 0, 0) ===
      fromUnixTime(b.createdOn).setHours(0, 0, 0, 0)
    ) {
      if (fromUnixTime(a.deadline) < fromUnixTime(b.deadline)) {
        return -1;
      } else if (fromUnixTime(a.deadline) < fromUnixTime(b.deadline)) {
        return 1;
      }
    }
    return 0;
  });

  const isComplete = goal.completedOn != null;
  const showStar = goal.isStarred && !isComplete;

  const restrictedForMe = goal.isRestricted && goal.userId === user.id;

  const canEdit =
    !restrictedForMe &&
    (relationships?.some((x) => x.to.id === userId && x.type === "manager") ||
      goal.userId === user.id);

  return (
    <>
      {editingObj != null && (
        <EditObjectiveModal
          value={editingObj === "new" ? null : editingObj}
          onClose={() => setEditingObj(null)}
          onSave={saveObj}
          fields={objFields}
          goalDeadline={goal.deadline}
        />
      )}
      <Row p={[2, 3]} borderTop={1}>
        <Col width={1 / 24}>
          {(canEdit || goal.objectives.length > 0) && (
            <IconButton
              icon={open ? FaChevronDown : FaChevronRight}
              color="body"
              onClick={() => setOpen((old) => !old)}
            />
          )}
          {open && <DownLine />}
        </Col>
        <Col width={[23 / 24, 8 / 24]}>
          {showStar && (
            <Circle size="small" color="star" icon={FaStar} ml={2} mr={2} />
          )}
          <Circle
            color="primary"
            icon={goal.type === "performance" ? FaMedal : FaLeaf}
            size="small"
            ml={showStar ? 0 : 2}
            mr={2}
            title={
              goal.type === "performance"
                ? `Performance ${terms.goal.asTitle()}`
                : `Personal Development ${terms.goal.asTitle()}`
            }
          />
          {goal.isRestricted && (
            <Circle
              size="small"
              color="grey"
              icon={FaLock}
              mr={2}
              title={`Restricted - only a line manager can update this ${terms.goal.asBody()}`}
            />
          )}
          <LinkButton onClick={() => onReview()} fontWeight={500} mr={2}>
            {goal.purpose}
          </LinkButton>
        </Col>
        <Col width={3 / 24} justifyContent="center" display={["none", "flex"]}>
          <Text fontSize="small" color="textMuted">
            {goal.updatedOn == null ? (
              "Never"
            ) : (
              <DateText value={goal.updatedOn} format="datetime" />
            )}
          </Text>
        </Col>
        <Col width={6 / 24} justifyContent="center" display={["none", "flex"]}>
          {goal.completedOn == null && goal.completionRequestedOn != null ? (
            <Label color="warning">Awaiting Approval</Label>
          ) : goal.completedOn == null ? (
            <GoalProgressDisplay
              onClick={() => onReview()}
              value={goal.completedOn != null ? "achieved" : goal.status}
              disabled={goal.completedOn != null || restrictedForMe}
            />
          ) : (
            getFriendlyName(goal.outcome)
          )}
        </Col>
        <Col width={2 / 24} justifyContent="center" display={["none", "flex"]}>
          <Flex ml={3} mr={3} alignItems={"center"} height={"30px"}>
            <PieChart
              startAngle={270}
              data={[
                {
                  value:
                    goal.progress > goal.target
                      ? 100
                      : Math.round((goal.progress / goal.target) * 100),
                  color: theme.colors.primary,
                },
                {
                  value:
                    goal.progress > goal.target
                      ? 0
                      : 100 - Math.round((goal.progress / goal.target) * 100),
                  color: theme.colors.grey4,
                },
              ]}
            />
            <Text
              width="80px"
              fontSize={2}
              ml={1}
              fontWeight={600}
              color="primary"
            >
              {Math.round((goal.progress / goal.target) * 100)} %
            </Text>
          </Flex>
        </Col>
        <Col width={2 / 12} justifyContent="center" display={["none", "flex"]}>
          <TimePeriod
            start={goal.createdOn}
            disabled={goal.completedOn != null || restrictedForMe}
            value={goal.deadline}
            onChange={setDeadline}
          />
        </Col>
        <Col width={1 / 24} justifyContent="flex-end">
          {canEdit && (
            <DotMenu
              items={[
                {
                  label: `${terms.goal.asTitle()} Details`,
                  icon: FaSearchPlus,
                  cmd: "review",
                },
                {
                  label: "Star",
                  icon: FaStar,
                  color: "body",
                  cmd: "star",
                  hidden: goal.isStarred || isComplete,
                },
                {
                  label: "Unstar",
                  icon: FaStar,
                  color: "body",
                  cmd: "star",
                  hidden: !goal.isStarred || isComplete,
                },
                {
                  label: "Delete",
                  icon: FaTrash,
                  color: "danger",
                  cmd: "delete",
                },
              ]}
              onItemClick={onItemClick}
            />
          )}
        </Col>
      </Row>
      {open && (
        <>
          {sortBy(sortedObjs, (x) => x.completedOn !== null).map((x, i) => (
            <Row p={[2, 3]} key={x.id ?? i}>
              <Col width={1 / 24}>
                {goal.completedOn != null &&
                i === goal.objectives.length - 1 ? (
                  <ActionLine />
                ) : (
                  <ObjLine />
                )}
              </Col>
              <Col width={goal.completedOn === null ? 8 / 24 : 7 / 24}>
                <Circle
                  ml={2}
                  color="orange"
                  icon={FaBullseye}
                  size="small"
                  mr={2}
                  title={terms.objective.asTitle()}
                />
                <Text
                  fontWeight={500}
                  style={{
                    textDecoration:
                      x.completedOn == null ? "none" : "line-through",
                  }}
                >
                  {x.purpose}
                </Text>
              </Col>
              <Col
                width={goal.completedOn === null ? 3 / 24 : 4 / 24}
                justifyContent="center"
                display={["none", "flex"]}
              >
                <Text fontSize="small" color="textMuted">
                  {x.updatedOn != null ? (
                    <DateText value={x.updatedOn} format="datetime" />
                  ) : null}
                </Text>
              </Col>
              {goal.completedOn !== null && (
                <Col
                  width={1 / 24}
                  justifyContent="center"
                  display={["none", "flex"]}
                ></Col>
              )}

              <Col
                width={goal.completedOn === null ? 6 / 24 : 4 / 24}
                justifyContent="center"
                display={["none", "flex"]}
              >
                {goal.completedOn == null &&
                goal.objectives.find((obj) => obj.id === x.id).completedOn ==
                  null ? (
                  <Link
                    title={`Review ${terms.goal.asTitle()}`}
                    to={null}
                    onClick={() => onReview()}
                  >
                    <GoalProgressDisplay
                      value={
                        goal.objectives.find((obj) => obj.id === x.id).status
                      }
                      disabled={false}
                    />
                  </Link>
                ) : (
                  <></>
                )}
              </Col>
              <Col
                width={2 / 24}
                justifyContent="center"
                display={["none", "flex"]}
              >
                <Flex alignItems={"center"} width={"70px"}>
                  <PieChart
                    startAngle={270}
                    data={[
                      {
                        value:
                          x.progress > x.target
                            ? 100
                            : Math.round((x.progress / x.target) * 100),
                        color: theme.colors.primary,
                      },
                      {
                        value:
                          x.progress > x.target
                            ? 0
                            : 100 - Math.round((x.progress / x.target) * 100),
                        color: theme.colors.grey4,
                      },
                    ]}
                  />
                  <Text
                    fontSize={2}
                    width={"80px"}
                    ml={1}
                    fontWeight={600}
                    color="primary"
                  >
                    {Math.round((x.progress / x.target) * 100)} %
                  </Text>
                </Flex>
              </Col>

              <Col
                width={4 / 24}
                justifyContent="center"
                display={["none", "flex"]}
              >
                <TimePeriod
                  value={x.deadline}
                  start={x.createdOn}
                  onChange={(newVal) => setObjectiveDeadline(x.id, newVal)}
                  disabled={x.completedOn != null || !canEdit}
                  maxValue={fromUnixTime(goal.deadline)}
                />
              </Col>
              <Col width={1 / 24} justifyContent="flex-end">
                {goal.completedOn == null && canEdit && (
                  <DotMenu
                    items={[
                      {
                        label: "Edit",
                        icon: FaPen,
                        disabled: x.completedOn != null,
                      },
                      {
                        label: "Delete",
                        icon: FaTrash,
                        color: "danger",
                      },
                    ]}
                    onItemClick={onObjectiveItemClick(x.id)}
                  />
                )}
              </Col>
            </Row>
          ))}
          {goal.completedOn == null && canEdit && (
            <Row p={[2, 3]}>
              <Col width={1 / 24}>
                <ActionLine />
              </Col>
              <Col width={23 / 24}>
                <Button
                  color="primary"
                  icon={FaBullseye}
                  tiny
                  onClick={() => setEditingObj("new")}
                >
                  Add {terms.objective.asTitle()}
                </Button>
              </Col>
            </Row>
          )}
        </>
      )}
    </>
  );
}

export type GoalListProps = {
  userId: string;
  reviewGoal: (goalId: string) => void;
  createGoal?: () => void;
};

export const GoalList = ({ userId, reviewGoal, createGoal }: GoalListProps) => {
  const { data: goals, isError } = useGoalsForUser(userId);
  const updateGoal = useUpdateGoal(userId);
  const starGoal = useStarGoal();
  const unstarGoal = useUnstarGoal();
  const deleteGoalMut = useDeleteGoal(userId);
  const terms = useGoalTerminology();

  const changeGoal = (goal: GoalDto) => {
    updateGoal.mutateAsync(goal);
  };

  const deleteGoal = async (id: string) => {
    if (window.confirm("Are you sure you want to delete this?")) {
      deleteGoalMut.mutateAsync(id);
    }
  };

  if (isError) {
    return (
      <Panel mb={3}>
        <WidgetHeader title={terms.goal.asPluralTitle()} />
        <EmptyState compact icon={FaLock}>
          You don't have access to these {terms.goal.asPluralBody()}. If you've
          just been added as their manager, they'll show here once the
          relationship is accepted.
        </EmptyState>
      </Panel>
    );
  }

  const sortedGoals = sortBy(goals, (x) => [
    !x.isStarred,
    x.completedOn == null ? x.deadline : Number.MAX_VALUE,
  ]);

  return (
    <>
      <Panel mb={3}>
        <WidgetHeader
          title={terms.goal.asPluralTitle()}
          icon={FaMedal}
          button={
            createGoal && {
              text: "Create Goal",
              icon: FaPlusCircle,
              onClick: createGoal,
            }
          }
        />
        <Flex width={1} p={2} borderBottom={1}>
          <Col width={12 / 24}>
            <Text fontWeight={600}>{terms.goal.asTitle()}</Text>
          </Col>
          <Col
            width={4 / 24}
            justifyContent="center"
            display={["none", "flex"]}
          >
            <Text fontWeight={600} textAlign="center">
              Last Status Update
            </Text>
          </Col>
          <Col
            width={8 / 24}
            justifyContent="center"
            display={["none", "flex"]}
          >
            <Text fontWeight={600} textAlign="center">
              Status
            </Text>
          </Col>
          <Col
            width={2 / 24}
            justifyContent="center"
            display={["none", "flex"]}
          >
            <Text fontWeight={600} textAlign="center">
              Progress
            </Text>
          </Col>
          <Col
            width={6 / 24}
            justifyContent="center"
            display={["none", "flex"]}
          >
            <Text fontWeight={600} textAlign="center">
              Time Period
            </Text>
          </Col>
          <Col width={1 / 24} />
        </Flex>
        {goals == null ? (
          <Loader />
        ) : (
          <Flex flexDirection={"column"}>
            {sortedGoals.filter((goal) => goal.completedOn === null).length ===
              0 && (
              <EmptyState compact icon={FaMedal}>
                No active {terms.goal.asPluralBody()} yet.
              </EmptyState>
            )}
            {sortedGoals
              .filter((goal) => goal.completedOn === null)
              .map((x, i) => (
                <GoalDisplay
                  goal={x}
                  key={i}
                  onChange={changeGoal}
                  onDelete={() => deleteGoal(x.id)}
                  onStar={() =>
                    x.isStarred
                      ? unstarGoal.mutateAsync(x.id)
                      : starGoal.mutateAsync(x.id)
                  }
                  onReview={() => reviewGoal(x.id)}
                  userId={userId}
                />
              ))}
          </Flex>
        )}
      </Panel>
      <Panel>
        <WidgetHeader
          title={`Completed/Closed ${terms.goal.asPluralTitle()}`}
          icon={FaMedal}
        />
        <Flex width={1} p={2} borderBottom={1}>
          <Col width={12 / 24}>
            <Text fontWeight={600}>{terms.goal.asTitle()}</Text>
          </Col>
          <Col
            width={5 / 24}
            justifyContent="center"
            display={["none", "flex"]}
          >
            <Text fontWeight={600} textAlign="center">
              Last Status Update
            </Text>
          </Col>
          <Col
            width={8 / 24}
            justifyContent="center"
            display={["none", "flex"]}
          >
            <Text fontWeight={600} textAlign="center">
              Outcome
            </Text>
          </Col>
          <Col
            width={2 / 24}
            justifyContent="center"
            display={["none", "flex"]}
          >
            <Text fontWeight={600} textAlign="center">
              Progress
            </Text>
          </Col>
          <Col
            width={6 / 24}
            justifyContent="center"
            display={["none", "flex"]}
          >
            <Text fontWeight={600} textAlign="center">
              Time Period
            </Text>
          </Col>
          <Col width={1 / 24} />
        </Flex>
        {goals == null ? (
          <Loader />
        ) : (
          <Flex flexDirection={"column"}>
            {sortedGoals.filter((goal) => goal.completedOn !== null).length ===
              0 && (
              <EmptyState compact icon={FaFlagCheckered}>
                Completed and closed {terms.goal.asPluralBody()} will show here.
              </EmptyState>
            )}
            {sortedGoals
              .filter((goal) => goal.completedOn !== null)
              .map((x, i) => (
                <GoalDisplay
                  goal={x}
                  key={i}
                  onChange={changeGoal}
                  onDelete={() => deleteGoal(x.id)}
                  onReview={() => reviewGoal(x.id)}
                  onStar={() =>
                    x.isStarred
                      ? unstarGoal.mutateAsync(x.id)
                      : starGoal.mutateAsync(x.id)
                  }
                  userId={userId}
                />
              ))}
          </Flex>
        )}
      </Panel>
    </>
  );
};
